import { ConflictsIcon } from "@/features/dashboard/pages/specification/components/reports-related-reusables/item-results-section/icons";
import { Timer, Users } from "lucide-react";
import { StarWave } from "./star-wave";
import { useDispatch } from "react-redux";
import { AppDispatch, useSelector } from "@/core/redux/store";
import { getConflictResolutionPerformance } from "@/core/redux/thunks/dashboardThunk";
import { useEffect } from "react";
import { formatDuration } from "@/shared/helpers/format-duration";
import { useTranslation } from "react-i18next";


export default function ConflictResolutionPerf() {
  const { i18n } = useTranslation();
  const conflictResolutionPerformance = useSelector((state) => state.dashboard.conflictResolutionPerformance);
  const percentage = conflictResolutionPerformance?.aiSuccessPercentage || 0;
  const totalSpecificationItem = conflictResolutionPerformance?.totalSpecificationItem || 0;
  const totalCitationCount = conflictResolutionPerformance?.totalCitationCount || 0;
  const totalSupplierCount = conflictResolutionPerformance?.totalSupplierCount || 0;
  const supplierSolvedItemCount = conflictResolutionPerformance?.supplierSolvedItemCount || 0;
  const overlappedItemCount = conflictResolutionPerformance?.overlappedItemCount || 0;
  const totalSupplierTimeSpent = conflictResolutionPerformance?.totalSupplierTimeSpent || 0;

  const userDetail = useSelector((state) => state.user.me);
  const company = userDetail?.company;

  const filterParams = useSelector((state) => state.dashboard.filterParams);
  const dispatch = useDispatch<AppDispatch>();
  useEffect(() => {
    if (!company?.id) return;
    dispatch(getConflictResolutionPerformance({ companyId: company.id, ...filterParams }));
  }, [company?.id, filterParams]);

  return (
    <div className="w-full flex flex-col border border-[#e5e5e5] bg-white rounded-[16px] h-[399px] overflow-hidden">
      <div className="border-b border-transparent h-[50px] flex items-center px-[20px] py-[16px]">
        <div className="flex items-start flex-1 h-full">
          <p className="text-[16px] font-[700] text-[#000000] leading-[24px]">
            Çakışma Çözüm Performansı
          </p>
        </div>
      </div>
      <div className="flex gap-[16px] px-[20px] pb-[18px] pt-[8px] h-full">
        <div className="w-[313px] p-[20px] rounded-[12px] h-full border border-[#e4e4e7] overflow-hidden relative">
          <div className="flex flex-col justify-between h-full">
            <div>
              <div className="text-[96px] font-[600] text-[#09090B] leading-[96px]">
                {percentage}%
              </div>
              <div className="text-[14px] font-[600] text-[#18181B] leading-[20px]">
                AI ile Çözümle
              </div>
              <div className="text-[14px] font-[700] text-muted-foreground leading-[20px]">
                {totalSpecificationItem} <span className="font-600">madde</span>
              </div>
            </div>
            <div>
              <div className="text-[20px] mb-[2px] font-[700] text-foreground ">
                {totalCitationCount}
              </div>
              <div className="text-[14px] font-[600] text-muted-foreground">
                Kaynak gösterimi
              </div>
            </div>
          </div>
          <div className="absolute bottom-[-2px] right-[-80px]">
            <StarWave progress={percentage} />
            <div className="absolute bottom-[45px] right-[210px]">
              <svg
                width="30"
                height="30"
                viewBox="0 0 30 30"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path d="M14.7917 1.5V28.0833V1.5Z" fill="#16A34A" />
                <path d="M28.0833 14.7917H1.5H28.0833Z" fill="#16A34A" />
                <path
                  d="M14.7917 1.5V28.0833M28.0833 14.7917H1.5"
                  stroke="#F8946D"
                  strokeWidth="3"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
          </div>
        </div>
        <div className="flex-1 flex flex-col gap-[16px] w-full">
          <div className="flex gap-[16px]">
            <div className="p-[16px] rounded-[12px] border border-[#e4e4e7] relative flex-1">
              <div className="text-[14px] text-[#3F3F46] font-600 mb-[6px] tracking-[-0.3px]">
                Çakışan Madde
              </div>
              <div className="text-[20px] font-[Inter] font-[700] text-foreground leading-[24px]">
                {overlappedItemCount}
              </div>
              <div className="absolute top-[8px] right-[10px]">
                <div className="size-[32px] rounded-full flex items-center justify-center border border-[#e4e4e7]">
                  <ConflictsIcon className="size-[13px]" color="#18181B" />
                </div>
              </div>
            </div>
            <div className="p-[16px] rounded-[12px] border border-[#e4e4e7] relative flex-1">
              <div className="text-[14px] text-[#3F3F46] font-600 mb-[6px] tracking-[-0.3px]">
                Çözüm süresi
              </div>
              <div className="text-[20px] font-[Inter] font-[700] text-foreground leading-[24px]">
                {formatDuration(i18n.language, totalSupplierTimeSpent)}
              </div>
              <div className="absolute top-[8px] right-[10px]">
                <div className="size-[32px] rounded-full flex items-center justify-center border border-[#e4e4e7]">
                  <Timer color="#18181B" size={13} />
                </div>
              </div>
            </div>
          </div>
          <div className="p-[16px] rounded-[12px] border border-[#e4e4e7] relative flex-1">
            <div className="text-[14px] text-[#3F3F46] font-600 mb-[4px] tracking-[-0.3px]">
              Danışman Desteği
            </div>
            <div className="flex gap-[8px] items-end">
              <div className="text-[24px] font-[Inter] font-[700] text-foreground leading-[32px]">
                {totalSupplierCount}
              </div>
              <div className="text-[14px] font-[600] text-muted-foreground leading-[24px]">
                kişi sayısı
              </div>
            </div>

            <div className="mt-[20px] flex flex-col gap-[16px]">
              <div className="flex flex-col gap-[8px]">
                <div className="flex justify-between items-center">
                  <div className="text-[#505D6F] text-[14px] font-600 leading-[20px] tracking-[-0.3px]">
                    Harcanan süre
                  </div>
                  <div className="inline-flex justify-start items-center gap-1.5">
                    <div className="inline-flex flex-col justify-start items-start">
                      <div className="justify-center text-[#18181B] text-sm font-semibold font-['Inter'] leading-5">
                        {formatDuration(i18n.language, totalSupplierTimeSpent)}
                      </div>
                    </div>
                    <div className="inline-flex flex-col justify-start items-start">
                      <div className="justify-center text-[#A1A1AA] text-sm font-normal font-['Inter'] leading-5">
                        ·
                      </div>
                    </div>
                    <div className="inline-flex flex-col justify-start items-start">
                      <div className="justify-center text-[#A1A1AA] text-sm font-normal font-['Inter'] leading-5">
                        {percentage}%
                      </div>
                    </div>
                  </div>
                </div>
                <div className="w-full bg-[#E4E4E766] rounded-full h-[10px]">
                  <div
                    className="bg-[#F76B6D] h-[10px] rounded-full transition-all duration-300"
                    style={{ width: "30%" }}
                  ></div>
                </div>
              </div>
              <div className="flex flex-col gap-[8px]">
                <div className="flex justify-between items-center">
                  <div className="text-[#505D6F] text-[14px] font-600 leading-[20px] tracking-[-0.3px]">
                    Çözülen madde
                  </div>
                  <div className="inline-flex justify-start items-center gap-1.5">
                    <div className="inline-flex flex-col justify-start items-start">
                      <div className="justify-center text-[#18181B] text-sm font-semibold font-['Inter'] leading-5">
                        {supplierSolvedItemCount}
                      </div>
                    </div>
                    <div className="inline-flex flex-col justify-start items-start">
                      <div className="justify-center text-[#A1A1AA] text-sm font-normal font-['Inter'] leading-5">
                        ·
                      </div>
                    </div>
                    <div className="inline-flex flex-col justify-start items-start">
                      <div className="justify-center text-[#A1A1AA] text-sm font-normal font-['Inter'] leading-5">
                        {supplierSolvedItemCount / totalSpecificationItem * 100}%
                      </div>
                    </div>
                  </div>
                </div>
                <div className="w-full bg-[#E4E4E766] rounded-full h-[10px]">
                  <div
                    className="bg-[#70BC66] h-[10px] rounded-full transition-all duration-300"
                    style={{ width: "5%" }}
                  ></div>
                </div>
              </div>
            </div>

            <div className="absolute top-[9px] right-[13px]">
              <div className="size-[40px] rounded-full flex items-center justify-center border border-[#e4e4e7]">
                <Users color="#18181B" size={16} />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
